import { useState, useEffect, useRef, useCallback } from 'react';
import { APP_NAME, usePageTitle } from '../appInfo';
import BrandMark from '../components/BrandMark';
import { Navigate, useNavigate, useLocation } from 'react-router-dom';
import { api } from '../api/api';
import { safeNext } from '../utils/safeNext';
import { formatServerTime } from '../utils/serverTime';

const POLL_INTERVAL_MS = 3000;

export default function Login() {
  usePageTitle('ログイン');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { search } = useLocation();
  const next = safeNext(search);

  const [serverStatus, setServerStatus] = useState('checking'); // checking | stopped | starting | ready | error
  const [scheduleInfo, setScheduleInfo] = useState(null);
  const [durationInput, setDurationInput] = useState('120');
  const [starting, setStarting] = useState(false);
  const timeoutRef = useRef(null);
  const cancelledRef = useRef(false);

  const scheduleNextPoll = useCallback(() => {
    if (cancelledRef.current) return;
    timeoutRef.current = setTimeout(async () => {
      const ok = await api.ping();
      if (cancelledRef.current) return;
      if (ok) {
        setServerStatus('ready');
      } else {
        scheduleNextPoll();
      }
    }, POLL_INTERVAL_MS);
  }, []);

  useEffect(() => {
    cancelledRef.current = false;

    // 状態確認のみ。stopped/stoppingでもここではwakeを呼ばず起動させない。
    // 既に他者の操作等でrunning/pending/rebootingになっている場合のみポーリングする。
    const checkStatus = async () => {
      try {
        const res = await api.getServerStatus();
        if (cancelledRef.current) return;
        setScheduleInfo(res);
        if (res.state === 'running') {
          const ok = await api.ping();
          if (cancelledRef.current) return;
          if (ok) {
            setServerStatus('ready');
          } else {
            setServerStatus('starting');
            scheduleNextPoll();
          }
        } else if (res.state === 'pending' || res.state === 'rebooting') {
          setServerStatus('starting');
          scheduleNextPoll();
        } else {
          setServerStatus('stopped');
        }
      } catch {
        if (!cancelledRef.current) setServerStatus('error');
      }
    };

    checkStatus();
    return () => { cancelledRef.current = true; clearTimeout(timeoutRef.current); };
  }, [scheduleNextPoll]);

  const handleStart = async () => {
    setStarting(true);
    try {
      const minutes = parseInt(durationInput, 10) || 120;
      const res = await api.wake(minutes);
      setScheduleInfo(res);
      setServerStatus('starting');
      scheduleNextPoll();
    } catch {
      setServerStatus('error');
    }
    setStarting(false);
  };

  const handleSetDuration = async () => {
    const minutes = parseInt(durationInput, 10);
    if (!minutes || minutes <= 0) return;
    try {
      const res = await api.wake(minutes);
      setScheduleInfo(res);
    } catch {
      // 反映できなくても致命的ではないので握りつぶす
    }
  };

  // 描画中の navigate() は react-router v7 では初回描画時に無視されるため、
  // <Navigate> で宣言的に飛ばす。
  if (api.checkAuth()) return <Navigate to={next} replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await api.login(username, password);
      navigate(next, { replace: true });
    } catch {
      setError('ユーザー名またはパスワードが違います');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100dvh' }}>
      <div className="modal" style={{ maxWidth: 360 }}>
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          gap: 10, marginBottom: 16,
        }}>
          <BrandMark size={72} />
          <h1 className="modal-title" style={{ margin: 0, letterSpacing: '0.04em' }}>{APP_NAME}</h1>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 8 }}>
          <span style={{
            display: 'inline-block', width: 10, height: 10, borderRadius: '50%',
            backgroundColor: serverStatus === 'ready' ? '#2ecc71'
              : serverStatus === 'starting' ? '#f1c40f'
              : '#ccc',
          }} />
          <span style={{ fontSize: 13 }}>
            {serverStatus === 'ready' && 'サーバー稼働中'}
            {serverStatus === 'starting' && 'サーバーを起動しています…'}
            {serverStatus === 'checking' && '確認中…'}
            {serverStatus === 'stopped' && 'サーバーは停止中です'}
            {serverStatus === 'error' && 'サーバー状態を取得できませんでした'}
          </span>
        </div>

        {serverStatus === 'starting' && (
          <p style={{ fontSize: 12, color: '#888', textAlign: 'center', marginBottom: 16 }}>数十秒ほどかかります</p>
        )}

        {serverStatus === 'stopped' && (
          <div style={{ textAlign: 'center', marginBottom: 16 }}>
            <button type="button" onClick={handleStart} className="btn btn-primary" disabled={starting}>
              {starting ? '起動中...' : 'サーバーを起動する'}
            </button>
          </div>
        )}

        {serverStatus === 'ready' && scheduleInfo && (
          <div style={{ fontSize: 12, color: '#888', textAlign: 'center', marginBottom: 12 }}>
            {scheduleInfo.scheduled_stop
              ? `稼働終了予定: ${formatServerTime(scheduleInfo.scheduled_stop)}`
              : `自動停止: ${scheduleInfo.default_stop ?? '22:00 (毎日)'}`}
            <div style={{ marginTop: 4 }}>
              <input
                type="number"
                min="1"
                placeholder="稼働時間(分)"
                value={durationInput}
                onChange={e => setDurationInput(e.target.value)}
                style={{ width: 90, marginRight: 4 }}
              />
              <button type="button" onClick={handleSetDuration} className="btn" style={{ padding: '2px 8px' }}>
                設定
              </button>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">ユーザー名</label>
            <input className="form-input" value={username} onChange={e => setUsername(e.target.value)} autoFocus disabled={serverStatus !== 'ready'} />
          </div>
          <div className="form-group">
            <label className="form-label">パスワード</label>
            <input className="form-input" type="password" value={password} onChange={e => setPassword(e.target.value)} disabled={serverStatus !== 'ready'} />
          </div>
          {error && <p className="error-msg">{error}</p>}
          <button className="btn btn-primary" style={{ width: '100%', marginTop: 8 }} disabled={loading || serverStatus !== 'ready'}>
            {loading ? 'ログイン中...' : 'ログイン'}
          </button>
        </form>
      </div>
    </div>
  );
}
